import { useMemo } from "react";
import { FilterInfo } from "@lhd/features/Apps/AppList/ManagementAppSheet/ManagementAppSheetList/AppSheetComponents/FilterIcon";
import { useFetchMaintenanceNoticeFilterOptions } from "@lhd/hooks";
import { getCompanyId } from "@lhd/utility";
import {
  buildActiveFilters,
  checkHasEmptyFilterBefore,
  convertFilterOrderToFieldKeys,
  getFieldKey,
  isMaintenanceNoticeDateField,
  normalizeFilterPayload,
  TargetTab,
} from "./fieldKeyMapping";

interface UseGetOptionsParams {
  filterInfo: FilterInfo;
  targetTab?: TargetTab;
  companyId?: string | number;
}

const useGetOptions = ({
  filterInfo,
  targetTab = "",
  companyId: customCompanyId,
}: UseGetOptionsParams) => {
  const companyId = customCompanyId || getCompanyId();
  const columnId = filterInfo.columnId ? String(filterInfo.columnId) : "";

  const fieldKey = useMemo(() => {
    if (!columnId) return null;
    return getFieldKey(columnId, targetTab);
  }, [columnId, targetTab]);

  const activeFiltersFieldKeys = useMemo(() => {
    return buildActiveFilters(filterInfo.filter, targetTab);
  }, [filterInfo.filter, targetTab]);

  const filterOrderFieldKeys = useMemo(() => {
    const convertedOrder = convertFilterOrderToFieldKeys(
      filterInfo.filterOrder || [],
      targetTab,
    );

    const validOrder = convertedOrder.filter((key) =>
      Object.keys(activeFiltersFieldKeys).includes(key),
    );

    Object.keys(activeFiltersFieldKeys).forEach((key) => {
      if (!validOrder.includes(key)) validOrder.unshift(key);
    });

    return validOrder;
  }, [filterInfo.filterOrder, targetTab, activeFiltersFieldKeys]);

  const normalizedPayload = useMemo(() => {
    return normalizeFilterPayload(fieldKey || "", {
      active_filters: activeFiltersFieldKeys,
      filter_order: filterOrderFieldKeys,
    });
  }, [activeFiltersFieldKeys, filterOrderFieldKeys, fieldKey]);

  const hasEmptyBefore = useMemo(
    () => checkHasEmptyFilterBefore(filterInfo, columnId),
    [filterInfo, columnId],
  );

  const { data: newApiData } = useFetchMaintenanceNoticeFilterOptions(
    fieldKey
      ? {
          field_names: [fieldKey],
          active_filters: normalizedPayload.active_filters,
        }
      : null,
    !!filterInfo?.columnId &&
      !!fieldKey &&
      !!filterInfo?.open &&
      !hasEmptyBefore,
  );

  const options = useMemo(() => {
    if (hasEmptyBefore) return { [columnId]: [] };
    if (!newApiData?.filter_options || !fieldKey) {
      return { [columnId]: [] };
    }

    const optionsArray = newApiData.filter_options[fieldKey] || [];
    const map = new Map<any, { label: string; value: any; count?: number }>();

    optionsArray.forEach((item: any) => {
      let value = String(item);
      let label = value;
      let count = undefined;

      if (typeof item === "object" && item !== null) {
        value = item.value !== undefined ? String(item.value) : String(item);
        label = item.label !== undefined ? String(item.label) : value;
        count = item.count;
      }

      if (value === "__NULL__") {
        map.set(value, {
          label: "未設定",
          value: value,
          count: count,
        });
      } else {
        if (isMaintenanceNoticeDateField(columnId)) {
          const strVal = String(value);
          if (/^\d{4}-\d{2}-\d{2}/.test(strVal)) {
            const datePart = strVal.split(" ")[0];
            const [year, month, day] = datePart.split("-");
            label = `${year}年${month}月${day}日`;
          } else if (/^\d{4}-\d{2}$/.test(strVal)) {
            const [year, month] = strVal.split("-");
            label = `${year}年${month}月`;
          } else if (/^\d{4}$/.test(strVal)) {
            label = `${strVal}年`;
          }
        }

        map.set(value, {
          label: label || String(value),
          value: value,
          count: count,
        });
      }
    });

    return { [columnId]: Array.from(map.values()) };
  }, [hasEmptyBefore, newApiData, fieldKey, columnId]);

  return { options };
};

export default useGetOptions;
